import { KeyValue } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges
} from '@angular/core';
import { VendorReview } from 'src/app/classes/models/vendor-review';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';

@Component({
  selector: 'app-vendor-review-questions',
  templateUrl: './vendor-review-questions.component.html',
  styleUrls: ['./vendor-review-questions.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class VendorReviewQuestionsComponent implements OnInit, OnChanges {
  constructor(
    private UtilityService: UtilityService,
    private ToastService: ToastService
  ) {}

  @Input() VendorReview?: VendorReview;
  @Input() readOnly: boolean = false;
  @Input() showOverallRatings: boolean = false;

  customerServiceRatings?: Map<
    string,
    { value: number | null | undefined; na: boolean }
  >;
  professionalismRatings?: Map<
    string,
    { value: number | null | undefined; na: boolean }
  >;
  overallCustomerServiceRatings: number = 0;
  overallProfessionalismRating: number = 0;

  keyValuePairDefaultOrder = this.UtilityService.keyValuePairDefaultOrder;

  calculateOverallVendorRating() {
    const calculateOverallRatings = (
      ratings: Map<string, { value: number | null | undefined; na: boolean }>
    ): number => {
      const ratingsArray = Array.from(ratings.entries()).filter(
        ([key, { value, na }]) => na === false
      );

      if (ratingsArray.every(([key, { value }]) => Number(value) === 0)) {
        return 0.0;
      }

      const sumOfRatings = ratingsArray
        .filter(([key, { value }]) => Number(value) !== null)
        .reduce(
          (acc, [key, { value: currentRating }]) => acc + Number(currentRating),
          0
        );
      const averageRating = sumOfRatings / ratingsArray.length;

      return parseFloat(averageRating.toFixed(2));
    };

    if (!this.VendorReview) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot calculate overall vendor rating. No vendor review found.',
        { returnError: true }
      );
    }

    if (!this.customerServiceRatings || !this.professionalismRatings) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot calculate overall vendor rating. No ratings found.',
        { returnError: true }
      );
    }

    this.overallCustomerServiceRatings = calculateOverallRatings(
      this.customerServiceRatings
    );
    this.overallProfessionalismRating = calculateOverallRatings(
      this.professionalismRatings
    );
    this.VendorReview.overallVendorRating = calculateOverallRatings(
      new Map([...this.customerServiceRatings, ...this.professionalismRatings])
    );
  }

  updateRating(
    rating: number | null,
    question: KeyValue<
      string,
      { value: number | null | undefined; na: boolean }
    >
  ) {
    if (!this.customerServiceRatings || !this.professionalismRatings) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot update rating. No ratings found.',
        { returnError: true }
      );
    }

    if (!this.VendorReview) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot update rating. No Vendor Review found.',
        { returnError: true }
      );
    }

    const questionsArray = [
      ...Array.from(this.customerServiceRatings.keys()),
      ...Array.from(this.professionalismRatings.keys())
    ];
    const questionIndex = questionsArray.findIndex(
      (key) => key === question.key
    );

    const customerServiceRatingsQuestion = this.customerServiceRatings.get(
      question.key
    );
    const professionalismRatingsQuestion = this.professionalismRatings.get(
      question.key
    );

    if (customerServiceRatingsQuestion) {
      this.customerServiceRatings.set(question.key, {
        value: rating,
        na: rating === null
      });
    }

    if (professionalismRatingsQuestion) {
      this.professionalismRatings.set(question.key, {
        value: rating,
        na: rating === null
      });
    }

    Object.assign(this.VendorReview, {
      [`question${questionIndex + 1}Score` as keyof VendorReview]:
        typeof rating === 'number'
          ? parseFloat(Number(rating).toFixed(2))
          : rating
    });

    this.calculateOverallVendorRating();
  }

  onNAChange(
    question: KeyValue<
      string,
      { value: number | null | undefined; na: boolean }
    >
  ) {
    question.value.na = !question.value.na;

    if (question.value.na) {
      this.updateRating(null, question);
    } else {
      this.updateRating(0, question);
    }
  }

  loadData() {
    if (!this.VendorReview) {
      this.VendorReview = new VendorReview({});
    }

    this.customerServiceRatings = new Map<
      string,
      { value: number | null | undefined; na: boolean }
    >([
      [
        'Arrives on time for projects or ongoing maintenance',
        {
          value: this.VendorReview.question1Score,
          na: this.VendorReview.question1Score === null
        }
      ],
      [
        'Courteous, timely, and informative communication',
        {
          value: this.VendorReview.question2Score,
          na: this.VendorReview.question2Score === null
        }
      ],
      [
        'Completes service/projects in a timely manner',
        {
          value: this.VendorReview.question3Score,
          na: this.VendorReview.question3Score === null
        }
      ],
      [
        'Leaves worksite clean',
        {
          value: this.VendorReview.question4Score,
          na: this.VendorReview.question4Score === null
        }
      ],
      [
        'Billing is accurate and timely',
        {
          value: this.VendorReview.question5Score,
          na: this.VendorReview.question5Score === null
        }
      ]
    ]);

    this.professionalismRatings = new Map<
      string,
      { value: number | null | undefined; na: boolean }
    >([
      [
        'Demonstrates knowledge of trade',
        {
          value: this.VendorReview.question6Score,
          na: this.VendorReview.question6Score === null
        }
      ],
      [
        'Performs work within cost estimates of the project',
        {
          value: this.VendorReview.question7Score,
          na: this.VendorReview.question7Score === null
        }
      ],
      [
        'Produces quality, finished work',
        {
          value: this.VendorReview.question8Score,
          na: this.VendorReview.question8Score === null
        }
      ],
      [
        'Professional appearance',
        {
          value: this.VendorReview.question9Score,
          na: this.VendorReview.question9Score === null
        }
      ],
      [
        'Jobs are performed within budget',
        {
          value: this.VendorReview.question10Score,
          na: this.VendorReview.question10Score === null
        }
      ]
    ]);
  }

  ngOnInit(): void {
    this.loadData();
    this.calculateOverallVendorRating();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['VendorReview']) {
      this.loadData();
      this.calculateOverallVendorRating();
    }
  }
}
